/**
 * Shared Socket.IO client singleton for the Admin app.
 *
 * Lifecycle is driven by the page that needs realtime — currently only
 * AdminSyncHealthPage (G2.25). Module load alone never opens a connection.
 *
 * Auth: HttpOnly cookies. The browser sends `access_token` automatically when
 * `withCredentials: true`. The backend `EventsGateway.extractToken` reads it
 * from `handshake.headers.cookie`. There is **no** identify handshake — the
 * gateway decodes the JWT at connect time and joins the appropriate
 * tenant/role/location rooms (and `platform:sync-health` for platform staff)
 * based on the token payload.
 *
 * Note: `apps/admin/src/hooks/use-dashboard-socket.ts` predates this module
 * and still creates its own `io()` connection for the live sales dashboard.
 * That is wasteful (a second socket per session) but harmless. Migrating it
 * is out of scope for G2.25 — see the plan file.
 */

import { io, type Socket } from 'socket.io-client';

export type SocketStatus = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'disconnected';

type StatusListener = (status: SocketStatus) => void;

let socket: Socket | null = null;
let status: SocketStatus = 'idle';
const statusListeners = new Set<StatusListener>();

function setStatus(next: SocketStatus): void {
  if (status === next) return;
  status = next;
  for (const fn of statusListeners) fn(status);
}

/**
 * Subscribe to status changes. Returns an unsubscribe function. Always emits
 * the current status synchronously on subscribe so consumers don't need to
 * race the first event.
 */
export function subscribeSocketStatus(fn: StatusListener): () => void {
  statusListeners.add(fn);
  fn(status);
  return () => {
    statusListeners.delete(fn);
  };
}

export function getSocketStatus(): SocketStatus {
  return status;
}

/**
 * Idempotent: returns the existing socket if already connected/connecting.
 * Re-binds the lifecycle event listeners on every call so React StrictMode
 * double-mounts don't leak handlers.
 */
export function connectSocket(): Socket {
  if (socket) return socket;

  setStatus('connecting');
  const next = io(window.location.origin, {
    withCredentials: true,
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10000,
    reconnectionAttempts: Infinity,
    autoConnect: true,
  });

  next.on('connect', () => setStatus('connected'));
  next.on('disconnect', () => setStatus('disconnected'));
  next.io.on('reconnect_attempt', () => setStatus('reconnecting'));
  next.io.on('reconnect', () => setStatus('connected'));
  next.io.on('reconnect_failed', () => setStatus('disconnected'));

  socket = next;
  return next;
}

export function getSocket(): Socket | null {
  return socket;
}

/**
 * Subscribe to a server event. Returns an unsubscribe. The hook layer is
 * responsible for calling `connectSocket()` first — this just attaches a
 * listener if a socket exists.
 */
export function onSocketEvent<T = unknown>(
  event: string,
  handler: (payload: T) => void,
): () => void {
  if (!socket) {
    return () => {
      /* no-op — nothing to detach */
    };
  }
  const wrapped = (payload: T) => handler(payload);
  socket.on(event, wrapped as (...args: unknown[]) => void);
  return () => {
    socket?.off(event, wrapped as (...args: unknown[]) => void);
  };
}

/**
 * Tear down the socket and clear the cached ref so the next connectSocket()
 * call rebuilds from scratch. Safe to call when no socket exists.
 */
export function disconnectSocket(): void {
  if (!socket) {
    if (status !== 'idle') setStatus('idle');
    return;
  }
  socket.removeAllListeners();
  socket.io.removeAllListeners();
  socket.disconnect();
  socket = null;
  setStatus('idle');
}
